// crossCover.js
// Derive CROSS-COVERS from a roster at read time.
//
// A cross-cover is one person holding two (or more) slots that share the same
// hours and shift type on the same date — MID 09:00–18:00 at WARD 6P and at
// WARD 7P, say. That identity is the "work block" the solver counts by
// (src/core/constraints/work_blocks.py) and the backend export derives with the
// same key (src/logic/cross_cover.js twin: src/logic/cross_cover.py). Keep the
// three in step: employee + start + end + shift type.
//
// Nothing is stamped on a shift — publishing persists only {staff_id, date, location} per slot —
// so deriving from the shifts is the only source that is always right.

// The holder's staff id: two holders are the same person only if their ids match.
const holderId = (s) => s?.employee?.id || null;
const typeOf = (s) => String(s?.shiftTypeId ?? s?.shift_type_id ?? '').trim().toLowerCase();

/** Block identity for an assigned shift/item; null when unassigned. */
export const blockKeyOf = (s) => {
  const id = holderId(s);
  if (!id) return null;
  return `${id}|${s.start || ''}|${s.end || ''}|${typeOf(s)}`;
};

/** { blockKey: [shifts] } over assigned shifts only, insertion-ordered. */
export const groupBlocks = (shifts) => {
  const groups = new Map();
  (shifts || []).forEach((s) => {
    const key = blockKeyOf(s);
    if (!key) return;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(s);
  });
  return groups;
};

/**
 * { shiftId: [sibling shift ids] } for every shift that is part of a
 * cross-cover (its block holds two or more slots). Others are absent.
 */
export const indexCrossCovers = (shifts) => {
  const out = {};
  groupBlocks(shifts).forEach((members) => {
    if (members.length < 2) return;
    const ids = members.map((m) => m.id);
    members.forEach((m) => { out[m.id] = ids.filter((i) => i !== m.id); });
  });
  return out;
};

/**
 * Collapse a list of one person's clinical items to one representative per
 * block, attaching `crossCoverLocations` (all wards in the block) so counters
 * count once and renderers can still show every ward.
 */
export const dedupeBlocks = (items) => {
  const out = [];
  groupBlocks(items).forEach((members) => {
    out.push({ ...members[0], crossCoverLocations: members.map((m) => m.location), crossCoverCount: members.length });
  });
  return out;
};
